/** Mirrors WpsPassDetails pass list ordering and counts. */
const BASE_PASS_ORDER = ['Root', 'Hot', 'Fill', 'Last Fill', 'Cap', 'Strip', 'Root Misfire'] as const
const MAX_FILL_COUNT = 21
const MAX_CAP_COUNT = 2

export type PassSectionInput = {
  passSequence?: string[]
  customPasses?: Array<string | { name: string; count?: string | number }>
  fillCount?: string | number
  capCount?: string | number
  stripCount?: string | number
} | null | undefined

/**
 * Expands saved pass configuration into one label per physical pass
 * (e.g. Fill 1, Fill 2, Cap 1) for tabs and deep links.
 */
export function expandPassSequenceToLabels(parsed: PassSectionInput): string[] {
  if (!parsed || typeof parsed !== 'object') return []
  const passSequence = Array.isArray(parsed.passSequence) ? parsed.passSequence : []
  const selected = new Set(passSequence)
  const customPasses = (parsed.customPasses || []).map((item) =>
    typeof item === 'string'
      ? { name: item, count: '1' }
      : { name: String(item.name), count: String(item.count ?? '1') }
  )
  const order: string[] = [...BASE_PASS_ORDER]
  for (const item of customPasses) {
    if (item.name && !order.includes(item.name)) order.push(item.name)
  }

  const fillCount = Math.min(
    MAX_FILL_COUNT,
    Math.max(0, Math.trunc(Number(parsed.fillCount ?? 0)))
  )
  const capCount = Math.min(MAX_CAP_COUNT, Math.max(0, Math.trunc(Number(parsed.capCount ?? 0))))
  const stripCount = Math.max(0, Math.trunc(Number(parsed.stripCount ?? 0)))

  const list: string[] = []
  order.forEach((pass) => {
    if (!selected.has(pass)) return
    if (pass === 'Fill') {
      for (let i = 0; i < fillCount; i++) list.push(`Fill ${i + 1}`)
      return
    }
    if (pass === 'Cap') {
      for (let i = 0; i < capCount; i++) list.push(`Cap ${i + 1}`)
      return
    }
    if (pass === 'Strip') {
      for (let i = 0; i < stripCount; i++) list.push(`Strip ${i + 1}`)
      return
    }
    if (pass === 'Root Misfire') {
      const custom = customPasses.find((item) => item.name === pass)
      const count = Math.max(1, Math.trunc(Number(custom?.count || 1)))
      for (let i = 0; i < count; i++) list.push(`Root Misfire ${i + 1}`)
      return
    }
    const custom = customPasses.find((item) => item.name === pass)
    if (custom) {
      const count = Math.max(0, Math.trunc(Number(custom.count || 0)))
      for (let i = 0; i < count; i++) list.push(`${custom.name} ${i + 1}`)
      return
    }
    list.push(pass)
  })
  return list
}

type PassSectionWithBevels = PassSectionInput & {
  bevelPasses?: PassSectionInput[]
}

/**
 * Returns the pass configuration for a given bevel index when `bevelPasses` exists;
 * otherwise returns the section object as a single-bevel payload.
 */
export function passSectionInputForBevel(
  passSection: unknown,
  bevelIndex: number
): PassSectionInput {
  if (!passSection || typeof passSection !== 'object') return null
  const parsed = passSection as PassSectionWithBevels
  if (Array.isArray(parsed.bevelPasses) && parsed.bevelPasses.length > 0) {
    const n = Number.isFinite(bevelIndex) ? Math.trunc(bevelIndex) : 0
    const clamped = Math.min(parsed.bevelPasses.length - 1, Math.max(0, n))
    return parsed.bevelPasses[clamped] ?? null
  }
  return parsed as PassSectionInput
}

/**
 * Returns the pass configuration for the active bevel of a `pass` section,
 * reading `activeBevelIndex` from the section itself. Use this from screens
 * that don't carry a bevel index in their URL (FloorSheet, WeldParameters).
 */
export function passSectionInputForActiveBevel(passSection: unknown): PassSectionInput {
  if (!passSection || typeof passSection !== 'object') return null
  const raw = (passSection as { activeBevelIndex?: unknown }).activeBevelIndex
  const idx =
    typeof raw === 'number' && Number.isFinite(raw)
      ? Math.trunc(raw)
      : typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))
        ? Math.trunc(Number(raw))
        : 0
  return passSectionInputForBevel(passSection, idx)
}

/** Persisted weldDetails.groups key for a pass on a given bevel (0-based). */
export function weldDetailsGroupStorageKey(bevelIndex: number, passLabel: string): string {
  return `b${bevelIndex}::${passLabel}`
}

/** Legacy weldDetails.groups keys before per-fill expansion. */
export function legacyWeldDetailsGroupKey(passGroup: string): string | null {
  if (/^Fill \d+$/.test(passGroup)) return 'Fill'
  if (/^Cap \d+$/.test(passGroup)) return 'Cap'
  if (/^Strip \d+$/.test(passGroup)) return 'Strip'
  const m = passGroup.match(/^(.+?) \d+$/)
  return m ? m[1] : null
}

function normalizeCustomPassesForSummary(
  value: PassSectionInput
): Array<{ name: string; count: string }> {
  if (!value || typeof value !== 'object') return []
  return (value.customPasses || [])
    .map((item) =>
      typeof item === 'string'
        ? { name: item, count: '1' }
        : { name: String(item.name), count: String(item.count ?? '1') }
    )
    .filter((item) => item.name.trim().length > 0)
}

function hasNonEmptyPassSequence(input: PassSectionInput): boolean {
  const sequence = input?.passSequence
  return Array.isArray(sequence) && sequence.length > 0
}

/**
 * Compact pass-sequence label for hub cards (e.g. "Root • Hot • Fill x17 • Cap").
 */
export function formatPassSequenceSummaryLabel(input: PassSectionInput): string {
  if (!input || typeof input !== 'object') return ''
  const customPasses = normalizeCustomPassesForSummary(input)
  const order = [...BASE_PASS_ORDER, ...customPasses.map((item) => item.name)]
  const selected = new Set(Array.isArray(input.passSequence) ? input.passSequence : [])

  const labelParts = order
    .filter((item) => selected.has(item))
    .map((item) => {
      if (item === 'Fill') {
        const count = Number(input.fillCount || 0)
        return count > 1 ? `Fill x${count}` : 'Fill'
      }
      if (item === 'Cap') {
        const count = Number(input.capCount || 0)
        return count > 1 ? `Cap x${count}` : 'Cap'
      }
      if (item === 'Strip') {
        const count = Number(input.stripCount || 0)
        return count > 1 ? `Strip x${count}` : 'Strip'
      }
      const custom = customPasses.find((pass) => pass.name === item)
      if (custom) {
        const count = Number(custom.count || 0)
        return count > 1 ? `${custom.name} x${count}` : custom.name
      }
      return item
    })

  return labelParts.join(' • ')
}

/**
 * Picks the bevel payload to summarize on hub cards: active bevel when it has
 * a sequence, otherwise the first bevel with a sequence, else legacy section.
 */
export function passInputForSummaryLabel(passSection: unknown): PassSectionInput {
  if (!passSection || typeof passSection !== 'object') return null
  const parsed = passSection as PassSectionWithBevels
  if (Array.isArray(parsed.bevelPasses) && parsed.bevelPasses.length > 0) {
    const active = passSectionInputForActiveBevel(passSection)
    if (hasNonEmptyPassSequence(active)) return active
    const firstWithSequence = parsed.bevelPasses.find((item) => hasNonEmptyPassSequence(item))
    if (firstWithSequence) return firstWithSequence
    return active
  }
  return parsed as PassSectionInput
}

/** Mirrors WpsPassDetails `countPassesFromPayload` for summary totals. */
export function countPassesFromInput(input: PassSectionInput): number {
  if (!input || typeof input !== 'object') return 0
  const selected = new Set(Array.isArray(input.passSequence) ? input.passSequence : [])
  const customPasses = normalizeCustomPassesForSummary(input)
  let total = 0
  if (selected.has('Root')) total += 1
  if (selected.has('Hot')) total += 1
  if (selected.has('Fill')) {
    total += Math.min(MAX_FILL_COUNT, Math.max(0, Number(input.fillCount || 0)))
  }
  if (selected.has('Cap')) {
    total += Math.min(MAX_CAP_COUNT, Math.max(0, Number(input.capCount || 0)))
  }
  if (selected.has('Strip')) total += Math.max(0, Number(input.stripCount || 0))
  if (selected.has('Root Misfire')) {
    const custom = customPasses.find((pass) => pass.name === 'Root Misfire')
    total += Math.max(1, Math.trunc(Number(custom?.count || 1)))
  }
  customPasses.forEach((pass) => {
    if (pass.name === 'Root Misfire') return
    if (selected.has(pass.name)) total += Math.max(0, Number(pass.count || 0))
  })
  return total
}

function sectionHasAnyPassSequence(passSection: unknown): boolean {
  if (!passSection || typeof passSection !== 'object') return false
  const parsed = passSection as PassSectionWithBevels & { passSequence?: string[] }
  if (hasNonEmptyPassSequence(parsed)) return true
  if (Array.isArray(parsed.bevelPasses)) {
    return parsed.bevelPasses.some((item) => hasNonEmptyPassSequence(item))
  }
  return false
}

function resolvePassCount(passSection: unknown): string {
  if (!passSection || typeof passSection !== 'object') return ''
  const parsed = passSection as PassSectionWithBevels & { passCount?: string | number }
  const topLevel = parsed.passCount
  if (topLevel !== undefined && topLevel !== null && String(topLevel).trim() !== '') {
    return String(topLevel)
  }
  if (Array.isArray(parsed.bevelPasses) && parsed.bevelPasses.length > 0) {
    const total = parsed.bevelPasses.reduce(
      (sum, item) => sum + countPassesFromInput(item),
      0
    )
    return String(total)
  }
  return String(countPassesFromInput(parsed as PassSectionInput))
}

export type PassSectionSummary = {
  sequenceLabel: string
  passCount: string
  isComplete: boolean
}

/** Hub-card summary for legacy flat or bevel-scoped pass sections. */
export function resolvePassSectionSummary(passSection: unknown): PassSectionSummary {
  const passCount = resolvePassCount(passSection)
  const sequenceLabel = formatPassSequenceSummaryLabel(passInputForSummaryLabel(passSection))
  const isComplete =
    Number(passCount) > 0 && sectionHasAnyPassSequence(passSection)
  return { sequenceLabel, passCount, isComplete }
}
